import { QueuePlaybackService } from "../services/QueuePlaybackService";
import React from "react";
import {
  IonButton,
  IonCol,
  IonIcon,
  IonList,
  IonProgressBar,
  IonText,
} from "@ionic/react";
import { arrowDownOutline, playOutline } from "ionicons/icons";
import { useVirtual } from "react-virtual";
import { usePlayer } from "../data/store/usePlayer";
import { usePlaylist } from "../data/store/usePlaylist";
import { songHookStatus, useSongs } from "../data/store/useSongs";
import { SongRef } from "../models/Song";
import MediaService from "../services/MediaService";
import SongItem from "./SongItem";
import Spacer from "./Spacer";

interface OwnProps {
  songRefs: SongRef[];
  fetchMore: () => void;
  onDeleteSong?: (id: string) => void;
  status: songHookStatus;
  nextPageText?: string;
  hideFetchButton?: boolean;
  showDeletedButton?: boolean;
  // When provided, "Play All" enables continuous (YouTube-style) autoplay: it
  // queues the visible page and, once that runs out, calls this to load the next
  // page so playback keeps going — instead of pre-queuing an entire (feed) or
  // whole-catalog (artist) list, which is slow and bloats the queue. Returns the
  // ids that were newly loaded.
  loadMore?: () => Promise<string[]>;
}

interface SongListProps extends OwnProps {}

const SongList: React.FC<SongListProps> = ({
  songRefs,
  status,
  fetchMore,
  hideFetchButton,
  showDeletedButton,
  nextPageText,
  loadMore,
  onDeleteSong = () => {},
}) => {
  const { queueSong, setNowPlaying, setLoadMore } = usePlaylist();
  const isPaused = usePlayer((x) => x.isPaused);
  const retrieve = useSongs((x) => x.retrieve);
  const parentRef = React.useRef<HTMLIonListElement>(null);

  const rowVirtualizer = useVirtual({
    size: songRefs.length,
    parentRef,
  });

  const playAll = () => {
    // Register continuous autoplay (feed/artist) or clear it (finite lists), so
    // playback continues past the visible page by loading more as it goes.
    setLoadMore(loadMore ?? null);
    if (songRefs.length === 0) return;
    songRefs.forEach((x) => queueSong(x.id));
    // Start at the first song this viewer can actually play (later entries
    // may not be loaded yet, or be members-only for them).
    if (isPaused) QueuePlaybackService.playFirst(songRefs.map((x) => x.id));
  };

  return (
    <>
      {songRefs.length > 1 && (
        <IonCol size="12" className="ion-no-padding">
          <IonButton
            fill="outline"
            color="favorite"
            expand="full"
            onClick={playAll}
            className="profile-action-button"
            style={{
              // Plain `padding` is real CSS padding on ion-button's host,
              // which shrinks the button's own border box inward on every
              // side (confirmed via getBoundingClientRect: the visible
              // border sat 5px inside the host's true edge) — that made
              // this button read as narrower than the compact-toggle row
              // above despite their outer boxes being pixel-aligned. The
              // --padding-* custom properties pad the button's *content*
              // instead, without moving its border box.
              "--padding-top": "5px",
              "--padding-bottom": "5px",
              "--padding-start": "5px",
              "--padding-end": "5px",
              margin: "0 16px",
              borderRadius: "8px",
            } as React.CSSProperties}
          >
            <IonText>
              Play All
            </IonText>
            <IonIcon class="ion-margin-horizontal" icon={playOutline} aria-hidden="true" />
          </IonButton>
        </IonCol>
      )}

      <IonList ref={parentRef} style={{ background: "clear", marginTop: 8 }} color="light">
        {rowVirtualizer.virtualItems.map((virtualRow) => {
          const songRef = songRefs[virtualRow.index];

          return (
            <div key={songRef.id}>
              <SongItem id={songRef.id} clearDeletedSong={onDeleteSong} />
              {showDeletedButton && (
                <IonButton
                  expand="full"
                  color="danger"
                  onClick={() => onDeleteSong(songRef.id)}
                >
                  Remove song from list
                </IonButton>
              )}
            </div>
          );
        })}
      </IonList>

      {!hideFetchButton && (
        <IonCol size="12">
          {status === "idle" ? (
            !hideFetchButton && (
              <IonButton
                fill="outline"
                color="tertiary"
                expand="full"
                onClick={fetchMore}
                className="profile-action-button"
                style={{
                  "--padding-top": "5px",
                  "--padding-bottom": "5px",
                  "--padding-start": "5px",
                  "--padding-end": "5px",
                  margin: "0 16px",
                  borderRadius: "8px",
                } as React.CSSProperties}
              >
                <IonText>
                  {nextPageText || "Load More"}
                </IonText>
                <IonIcon icon={arrowDownOutline} aria-hidden="true" />
              </IonButton>
            )
          ) : (
            <IonProgressBar
              style={{ height: 52 }}
              type="indeterminate"
              color="tertiary"
            />
          )}
        </IonCol>
      )}
      <Spacer height={100} />
    </>
  );
};

export default SongList;
